/**
 * Item-selector Dialog for the create wizard. Searches warehouse items and adds
 * chosen ones to the process line-item list. Selection lives in the parent
 * wizard; this dialog only surfaces candidates and reports adds.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  Input,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { useItems, type Item } from '@/api/capacities';

const muted = { color: 'var(--vn-fg-muted)' } as const;

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

export interface ItemSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called for each item the user picks. */
  onAdd: (item: Item) => void;
  /** IDs already in the selection (rendered as disabled). */
  selectedIds: string[];
}

export function ItemSelectorDialog({
  open,
  onOpenChange,
  onAdd,
  selectedIds,
}: ItemSelectorDialogProps) {
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebounced(rawQuery, 400);
  const { data, isLoading } = useItems({ page_size: 25, search: query || undefined });
  const rows = data?.results ?? [];

  const columns = useMemo<ColumnDef<Item, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Item',
        cell: ({ row }) => (
          <span title={row.original.name} style={{ overflowWrap: 'anywhere' }}>
            {row.original.name}
          </span>
        ),
      },
      {
        id: 'ref',
        header: 'Tag / Ref',
        cell: ({ row }) => {
          const ref = row.original.service_tag || row.original.ref;
          return ref ? ref : <span style={muted}>—</span>;
        },
      },
      {
        id: 'add',
        header: '',
        cell: ({ row }) => {
          const already = selectedIds.includes(row.original.id);
          return (
            <Button
              size="sm"
              variant="secondary"
              disabled={already}
              startIcon={<Plus size={14} />}
              onClick={() => onAdd(row.original)}
            >
              {already ? 'Added' : 'Add'}
            </Button>
          );
        },
      },
    ],
    [onAdd, selectedIds],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Add items</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Input
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              placeholder="Search items…"
              startSlot={<Search />}
            />
            {isLoading ? (
              <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} style={{ height: 24 }} />
                ))}
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={rows}
                getRowId={(r) => r.id}
                emptyState={
                  <EmptyState
                    icon={<Search size={20} />}
                    title="No items"
                    description="Try a different search."
                  />
                }
              />
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">Done</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
